import React, { useEffect, useState } from 'react';
import {
  Select,
  MenuItem,
  ListItemText,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { useDispatch } from 'react-redux';
import { invokeGetService, toggleSimulationRun } from '../../../store/global/actions';
import socket from '../../../websocket';
import { EVENTS } from '../../../websocket/events';

const STATUS = {
  "completed": { color: '#2f9e5c', label: 'Success' },
  failed: { color: '#d64545', label: 'Failed' },
  error: { color: '#d64545', label: 'Failed' },
  running: { color: '#e08a1e', label: 'Running' },
};

const useStyles = makeStyles(() => ({
  select: {
    border: '1px solid #d6d9df',
    borderRadius: 8,
    backgroundColor: '#fff',
  },
  selectInput: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 6px 6px 10px',
    fontSize: 12.5,
    fontWeight: 500,
    color: '#1a1d21',
  },
  paper: {
    marginTop: 4,
    borderRadius: 8,
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    fontSize: 13.5,
  },
  itemLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  runLabel: {
    fontSize: 13.5,
    color: '#1a1d21',
  },
  statusRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 500,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 14px',
    borderTop: '1px solid #eef0f3',
    fontSize: 12.5,
    color: '#8a8f98',
  },
  iconSm: {
    fontSize: 18,
  },
}));

function StatusIcon({ status, className }) {
  if (status === 'running') {
    return (
      <CircularProgress
        id="simulation-run-progress"
        size={12.5}
        thickness={3}
        value={70}
        style={{ color: STATUS.running.color }}
      />
    );
  }
  const Icon = status === "completed" ? CheckCircleIcon : CancelIcon;
  return <Icon className={className} style={{ color: STATUS[status]?.color, fontSize: 14 }} />;
}

export default function SimulationRunSelect({
  selectedRunId,
  setRunId,
  isSimulationRunning
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [runs, setRuns] = useState([]);
  const selectedRun = runs.find((r) => r._id === selectedRunId?._id);

  const fetchBatches = async () => {
    try{
        const response = await dispatch(invokeGetService('getSimulationBatches', {}, true));
        setRuns(response.batches || [])
        if(!selectedRunId || isSimulationRunning === "live") setRunId((response.batches || [])?.[0])
    }catch(error){
        console.error(error)
    }
  } 
  
  useEffect(() => {
    fetchBatches();
  },[isSimulationRunning])

  useEffect(() => {
    const handleStop = () => {
      fetchBatches();
      dispatch(toggleSimulationRun(false))
    };

    socket.on(EVENTS.SIMULATION_STOPPED, handleStop);

    return () => {
        socket.off(EVENTS.SIMULATION_STOPPED, handleStop);
    };
  }, []);

  const onSelectRun = (value) => setRunId(runs.find(run => run._id === value))
  
  return (
    <Select
      value={selectedRun?._id || ''}
      onChange={(e) => onSelectRun(e.target.value)}
      className={classes.select}
      classes={{ select: classes.selectInput }}
      disableUnderline
      displayEmpty
      id="simulation-run-select"
      MenuProps={{
        classes: { paper: classes.paper },
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        getContentAnchorEl: null,
      }}
      renderValue={() => {
        if (!selectedRun) {
          return <span className={classes.itemLeft} style={{ color: '#8a8f98' }}>Select a run</span>;
        }
        return (
          <span className={classes.itemLeft}>
            <StatusIcon status={selectedRun.status} />
            {selectedRun.label}
          </span>
        );
      }}
    >
      {runs.map((run) => (
        <MenuItem key={run._id} value={run._id} className={`${classes.menuItem} simulation-history-item`}>
          <span className={classes.itemLeft}>
            <StatusIcon status={run.status} />
            <span className={classes.runLabel}>{run.label}</span>
          </span>
        </MenuItem>
      ))}
    </Select>
  );
}